/**
 * Karte mit auszahlbarem Saldo + Button "Auszahlung anfordern".
 * Validiert lokal Mindestbetrag und vollständige Stammdaten.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Loader2, Wallet } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { AffiliateRecord } from "@/hooks/useMyAffiliate";

const formatEur = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

interface Props {
  affiliate: AffiliateRecord;
}

const PartnerRequestPayoutCard = ({ affiliate }: Props) => {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data: balance } = useQuery({
    queryKey: ["partner-payable-balance", affiliate.id],
    queryFn: async () => {
      // Auszahlbar = approved-Provisionen ohne payout_id
      const { data, error } = await supabase
        .from("affiliate_commissions")
        .select("commission_amount")
        .eq("affiliate_id", affiliate.id)
        .eq("status", "approved")
        .is("payout_id", null);
      if (error) throw error;
      const sum = (data ?? []).reduce((acc, r) => acc + Number(r.commission_amount ?? 0), 0);
      return sum;
    },
    staleTime: 30_000,
  });

  const { data: settings } = useQuery({
    queryKey: ["affiliate-settings-min-payout"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_settings")
        .select("min_payout_cents")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });

  const minPayout = Number(settings?.min_payout_cents ?? 2500);
  const available = balance ?? 0;

  const missing: string[] = [];
  if (!affiliate.payout_iban) missing.push("IBAN");
  if (!affiliate.billing_full_name) missing.push("Name");
  if (!affiliate.billing_street || !affiliate.billing_zip || !affiliate.billing_city) missing.push("Adresse");
  if (!affiliate.tax_status) missing.push("USt-Status");
  // USt-ID ist optional – auch für umsatzsteuerpflichtige Partner nicht erforderlich.

  const canRequest = available >= minPayout && missing.length === 0;

  const handleRequest = async () => {
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("affiliate-request-payout");
      if (error) throw error;
      toast({ title: "Auszahlung angefordert", description: "Wir bearbeiten deine Anfrage zeitnah." });
      await qc.invalidateQueries({ queryKey: ["partner-payable-balance", affiliate.id] });
      await qc.invalidateQueries({ queryKey: ["partner-payouts", affiliate.id] });
    } catch (err) {
      toast({ title: "Fehler", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary" /> Provisions-Saldo
        </CardTitle>
        <CardDescription>
          Deine Provisionen werden gesammelt. Ab {formatEur(minPayout)} kannst du jederzeit eine Auszahlung anfordern. Du bekommst dann automatisch eine Gutschrift als PDF.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-3xl font-bold">{formatEur(available)}</div>
          <div className="text-sm text-muted-foreground">auszahlbar (freigegebene Provisionen)</div>
        </div>

        {missing.length > 0 && (
          <Alert variant="default" className="border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Daten unvollständig</AlertTitle>
            <AlertDescription>
              Bitte ergänze unten noch: {missing.join(", ")}.
            </AlertDescription>
          </Alert>
        )}

        {missing.length === 0 && available < minPayout && (
          <p className="text-sm text-muted-foreground">
            Noch {formatEur(minPayout - available)} bis zur Mindestauszahlung.
          </p>
        )}

        <Button onClick={handleRequest} disabled={!canRequest || submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Auszahlung anfordern
        </Button>
      </CardContent>
    </Card>
  );
};

export default PartnerRequestPayoutCard;
